/**
 * Axios API client with Cognito auth interceptors.
 * Uses getAuthToken/clearAuthToken from @amzn/neura-web-shared-components.
 * AuthProvider populates the token store after fetchAuthSession() succeeds.
 */

import axios from 'axios';
import { getAuthToken, clearAuthToken } from '@amzn/neura-web-shared-components';

export const apiClient = axios.create({
  baseURL: '', // Set at runtime via setApiBaseUrl() after config.json loads
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

/** Called from bootstrap() once runtime config is available */
export function setApiBaseUrl(url: string): void {
  apiClient.defaults.baseURL = url;
}

// Request interceptor — inject Cognito ID token
apiClient.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor — handle auth errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired — clear and let AuthProvider handle re-auth via Amplify
      clearAuthToken();
    }
    // 403 handled at component level via ProtectedRoute
    return Promise.reject(error);
  }
);

export default apiClient;
